import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import dsWikiService from '../services/dsWikiService';
import { PageSkeleton } from '@/components/base/Skeleton';

interface TableCell {
  content: string;
  isHeader?: boolean;
}

interface ImageData {
  src: string;
  alt: string;
  caption?: string;
  alignment: 'left' | 'center' | 'right' | 'full';
}

interface ContentBlock {
  id: string;
  type: 'h1' | 'h2' | 'h3' | 'paragraph' | 'list' | 'code' | 'quote' | 'divider' | 'table' | 'image';
  content: string;
  items?: string[]; // For list type
  tableData?: TableCell[][]; // For table type [rows][cols]
  imageData?: ImageData; // For image type
}

interface HistoryState {
  article: ArticleData;
  timestamp: number;
}

interface SelectionToolbar {
  show: boolean;
  x: number;
  y: number;
  blockId: string | null;
}

interface TextFormat {
  bold: boolean;
  italic: boolean;
  underline: boolean;
}

interface ArticleData {
  id: string;
  title: string;
  subtitle: string;
  summary: string;
  blocks: ContentBlock[];
  tags: string[];
  status: 'draft' | 'under_curation' | 'published';
}

interface LinkedArticle {
  id: string;
  title: string;
  tags: string[];
  status: 'draft' | 'under_curation' | 'published';
}

interface ArticleLinkModalState {
  show: boolean;
  searchQuery: string;
  selectedTags: string[];
}

type EditorMode = 'visual' | 'markdown';

// Mock existing articles for linking
const existingArticles: LinkedArticle[] = [
  { id: '101', title: 'Machine Learning Fundamentals', tags: ['machine-learning', 'ai', 'algorithms'], status: 'published' },
  { id: '102', title: 'Deep Learning Neural Networks', tags: ['deep-learning', 'ai', 'neural-networks'], status: 'published' },
  { id: '103', title: 'Natural Language Processing Guide', tags: ['nlp', 'ai', 'machine-learning'], status: 'published' },
  { id: '104', title: 'Computer Vision Techniques', tags: ['computer-vision', 'ai', 'image-processing'], status: 'published' },
  { id: '105', title: 'Reinforcement Learning Basics', tags: ['reinforcement-learning', 'ai', 'algorithms'], status: 'published' },
  { id: '106', title: 'Data Science Best Practices', tags: ['data-science', 'analytics', 'python'], status: 'published' },
  { id: '107', title: 'Python for Data Analysis', tags: ['python', 'data-science', 'pandas'], status: 'published' },
  { id: '108', title: 'Statistical Methods Overview', tags: ['statistics', 'data-science', 'analytics'], status: 'published' },
  { id: '109', title: 'Big Data Processing with Spark', tags: ['big-data', 'spark', 'data-engineering'], status: 'draft' },
  { id: '110', title: 'Cloud Computing Fundamentals', tags: ['cloud', 'aws', 'infrastructure'], status: 'published' },
  { id: '111', title: 'DevOps Pipeline Setup', tags: ['devops', 'ci-cd', 'automation'], status: 'under_curation' },
  { id: '112', title: 'Kubernetes Deployment Guide', tags: ['kubernetes', 'devops', 'containers'], status: 'published' },
];

// Extract all unique tags from existing articles
const allAvailableTags = Array.from(new Set(existingArticles.flatMap(a => a.tags))).sort();

export default function ArticleEdit() {
  const { articleId } = useParams<{ articleId: string }>();
  const navigate = useNavigate();
  const [editorMode, setEditorMode] = useState<EditorMode>('visual');
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [activeBlockId, setActiveBlockId] = useState<string | null>(null);
  const [showBlockMenu, setShowBlockMenu] = useState(false);
  const [blockMenuPosition, setBlockMenuPosition] = useState({ x: 0, y: 0 });

  // Selection toolbar state
  const [selectionToolbar, setSelectionToolbar] = useState<SelectionToolbar>({
    show: false,
    x: 0,
    y: 0,
    blockId: null
  });
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Article link modal state
  const [articleLinkModal, setArticleLinkModal] = useState<ArticleLinkModalState>({
    show: false,
    searchQuery: '',
    selectedTags: []
  });
  const [savedSelection, setSavedSelection] = useState<Range | null>(null);

  // Undo/Redo history state
  const [history, setHistory] = useState<HistoryState[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isUndoRedo, setIsUndoRedo] = useState(false);
  const maxHistorySize = 50;

  // Markdown content for markdown mode
  const [markdownContent, setMarkdownContent] = useState('');

  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Article data - start with empty state
  const [article, setArticle] = useState<ArticleData>({
    id: articleId || '',
    title: '',
    subtitle: '',
    summary: '',
    blocks: [],
    tags: [],
    status: 'draft'
  });

  // Check if this is a new article (articleId is "new" or not provided)
  const isNewArticle = !articleId || articleId === 'new';

  // Fetch article data from API (only for existing articles)
  useEffect(() => {
    const fetchArticle = async () => {
      // For new articles, skip fetching and just set loading to false
      if (isNewArticle) {
        setArticle({
          id: '',
          title: '',
          subtitle: '',
          summary: '',
          blocks: [{ id: '1', type: 'paragraph', content: '' }],
          tags: [],
          status: 'draft'
        });
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setLoadError(null);
        const response = await dsWikiService.getArticleById(parseInt(articleId, 10));

        if (response.status === 1 && response.data) {
          const apiArticle = response.data;
          // Transform API response to ArticleData format
          const blocks: ContentBlock[] = [];

          // Parse content if it exists (assuming content is stored as JSON or markdown)
          if (apiArticle.content) {
            try {
              // Try to parse as JSON blocks first
              const parsedContent = JSON.parse(apiArticle.content);
              if (Array.isArray(parsedContent)) {
                blocks.push(...parsedContent);
              }
            } catch {
              // If not JSON, create a paragraph block with the content
              blocks.push({
                id: '1',
                type: 'paragraph',
                content: apiArticle.content
              });
            }
          }

          setArticle({
            id: String(apiArticle.id),
            title: apiArticle.title || '',
            subtitle: apiArticle.subtitle || '',
            summary: apiArticle.summary || '',
            blocks: blocks.length > 0 ? blocks : [{ id: '1', type: 'paragraph', content: '' }],
            tags: apiArticle.tags || [],
            status: apiArticle.status?.code || 'draft'
          });
        } else {
          setLoadError(response.message || 'Failed to load article');
        }
      } catch (error) {
        console.error('Error fetching article:', error);
        setLoadError('Failed to load article. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchArticle();
  }, [articleId, isNewArticle]);

  // Push to history when article changes (debounced)
  useEffect(() => {
    if (isUndoRedo) {
      setIsUndoRedo(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      setHistory(prev => {
        // Remove any future history if we're not at the end
        const newHistory = prev.slice(0, historyIndex + 1);

        // Add new state
        const newState: HistoryState = {
          article: JSON.parse(JSON.stringify(article)),
          timestamp: Date.now()
        };

        // Limit history size
        if (newHistory.length >= maxHistorySize) {
          newHistory.shift();
        }

        return [...newHistory, newState];
      });
      setHistoryIndex(prev => Math.min(prev + 1, maxHistorySize - 1));
    }, 500); // Debounce 500ms

    return () => clearTimeout(timeoutId);
  }, [article.blocks, article.title, article.subtitle, article.summary]);

  // Initialize history with initial state (only after article is loaded)
  useEffect(() => {
    if (!isLoading && history.length === 0) {
      setHistory([{ article: JSON.parse(JSON.stringify(article)), timestamp: Date.now() }]);
      setHistoryIndex(0);
    }
  }, [isLoading]);

  // Undo function
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      setIsUndoRedo(true);
      const prevState = history[historyIndex - 1];
      setArticle(JSON.parse(JSON.stringify(prevState.article)));
      setHistoryIndex(historyIndex - 1);
    }
  }, [history, historyIndex]);

  // Redo function
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      setIsUndoRedo(true);
      const nextState = history[historyIndex + 1];
      setArticle(JSON.parse(JSON.stringify(nextState.article)));
      setHistoryIndex(historyIndex + 1);
    }
  }, [history, historyIndex]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'y') {
        e.preventDefault();
        redo();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  // Convert blocks to markdown
  const blocksToMarkdown = useCallback((blocks: ContentBlock[]) => {
    return blocks.map(block => {
      switch (block.type) {
        case 'h1': return `# ${block.content}`;
        case 'h2': return `## ${block.content}`;
        case 'h3': return `### ${block.content}`;
        case 'paragraph': return block.content;
        case 'list': return block.items?.map(item => `- ${item}`).join('\n') || '';
        case 'code': return `\`\`\`\n${block.content}\n\`\`\``;
        case 'quote': return `> ${block.content}`;
        case 'divider': return '---';
        default: return block.content;
      }
    }).join('\n\n');
  }, []);

  // Convert markdown to blocks
  const markdownToBlocks = useCallback((markdown: string): ContentBlock[] => {
    const lines = markdown.split('\n');
    const blocks: ContentBlock[] = [];
    let currentBlock: ContentBlock | null = null;
    let inCodeBlock = false;
    let codeContent = '';
    let listItems: string[] = [];
    let blockId = 1;

    lines.forEach((line) => {
      // Handle code blocks
      if (line.startsWith('```')) {
        if (inCodeBlock) {
          blocks.push({ id: String(blockId++), type: 'code', content: codeContent.trim() });
          codeContent = '';
          inCodeBlock = false;
        } else {
          inCodeBlock = true;
        }
        return;
      }

      if (inCodeBlock) {
        codeContent += line + '\n';
        return;
      }

      // Handle list items
      if (line.match(/^[-*]\s/)) {
        listItems.push(line.replace(/^[-*]\s/, ''));
        return;
      } else if (listItems.length > 0) {
        blocks.push({ id: String(blockId++), type: 'list', content: '', items: [...listItems] });
        listItems = [];
      }

      // Handle headings
      if (line.startsWith('### ')) {
        blocks.push({ id: String(blockId++), type: 'h3', content: line.replace('### ', '') });
      } else if (line.startsWith('## ')) {
        blocks.push({ id: String(blockId++), type: 'h2', content: line.replace('## ', '') });
      } else if (line.startsWith('# ')) {
        blocks.push({ id: String(blockId++), type: 'h1', content: line.replace('# ', '') });
      } else if (line.startsWith('> ')) {
        blocks.push({ id: String(blockId++), type: 'quote', content: line.replace('> ', '') });
      } else if (line === '---') {
        blocks.push({ id: String(blockId++), type: 'divider', content: '' });
      } else if (line.trim()) {
        blocks.push({ id: String(blockId++), type: 'paragraph', content: line });
      }
    });

    // Handle remaining list items
    if (listItems.length > 0) {
      blocks.push({ id: String(blockId++), type: 'list', content: '', items: listItems });
    }

    return blocks;
  }, []);

  // Initialize markdown content (only after article is loaded)
  useEffect(() => {
    if (!isLoading && article.blocks.length > 0) {
      setMarkdownContent(blocksToMarkdown(article.blocks));
    }
  }, [isLoading, article.blocks, blocksToMarkdown]);

  // Sync modes when switching
  const handleModeSwitch = (mode: EditorMode) => {
    if (mode === 'markdown' && editorMode === 'visual') {
      setMarkdownContent(blocksToMarkdown(article.blocks));
    } else if (mode === 'visual' && editorMode === 'markdown') {
      setArticle(prev => ({ ...prev, blocks: markdownToBlocks(markdownContent) }));
    }
    setEditorMode(mode);
  };

  // Update block content
  const updateBlock = (blockId: string, content: string) => {
    setArticle(prev => ({
      ...prev,
      blocks: prev.blocks.map(block =>
        block.id === blockId ? { ...block, content } : block
      )
    }));
  };

  // Update block type
  const changeBlockType = (blockId: string, newType: ContentBlock['type']) => {
    setArticle(prev => ({
      ...prev,
      blocks: prev.blocks.map(block => {
        if (block.id !== blockId) return block;

        // Initialize type-specific data when changing types
        const updatedBlock: ContentBlock = {
          ...block,
          type: newType,
          // Clear old type-specific data
          items: undefined,
          tableData: undefined,
          imageData: undefined
        };

        // Initialize new type-specific data
        if (newType === 'list' && !block.items) {
          updatedBlock.items = [block.content || ''];
        }
        if (newType === 'table' && !block.tableData) {
          updatedBlock.tableData = [
            [{ content: 'Header 1', isHeader: true }, { content: 'Header 2', isHeader: true }, { content: 'Header 3', isHeader: true }],
            [{ content: '' }, { content: '' }, { content: '' }],
            [{ content: '' }, { content: '' }, { content: '' }]
          ];
        }
        if (newType === 'image' && !block.imageData) {
          updatedBlock.imageData = {
            src: '',
            alt: '',
            caption: '',
            alignment: 'center'
          };
        }

        return updatedBlock;
      })
    }));
    setShowBlockMenu(false);
  };

  // Add new block after current
  const addBlockAfter = (afterId: string, type: ContentBlock['type'] = 'paragraph') => {
    const newBlock: ContentBlock = {
      id: `new-${Date.now()}`,
      type,
      content: '',
      items: type === 'list' ? [''] : undefined,
      tableData: type === 'table' ? [
        [{ content: 'Header 1', isHeader: true }, { content: 'Header 2', isHeader: true }, { content: 'Header 3', isHeader: true }],
        [{ content: '' }, { content: '' }, { content: '' }],
        [{ content: '' }, { content: '' }, { content: '' }]
      ] : undefined,
      imageData: type === 'image' ? {
        src: '',
        alt: '',
        caption: '',
        alignment: 'center'
      } : undefined
    };
    setArticle(prev => {
      const index = prev.blocks.findIndex(b => b.id === afterId);
      const newBlocks = [...prev.blocks];
      newBlocks.splice(index + 1, 0, newBlock);
      return { ...prev, blocks: newBlocks };
    });
    setTimeout(() => setActiveBlockId(newBlock.id), 0);
  };

  // Delete block
  const deleteBlock = (blockId: string) => {
    setArticle(prev => ({
      ...prev,
      blocks: prev.blocks.filter(b => b.id !== blockId)
    }));
  };

  // Move block up/down
  const moveBlock = (blockId: string, direction: 'up' | 'down') => {
    setArticle(prev => {
      const index = prev.blocks.findIndex(b => b.id === blockId);
      if ((direction === 'up' && index === 0) || (direction === 'down' && index === prev.blocks.length - 1)) {
        return prev;
      }
      const newBlocks = [...prev.blocks];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      [newBlocks[index], newBlocks[targetIndex]] = [newBlocks[targetIndex], newBlocks[index]];
      return { ...prev, blocks: newBlocks };
    });
  };

  // Update list item
  const updateListItem = (blockId: string, itemIndex: number, content: string) => {
    setArticle(prev => ({
      ...prev,
      blocks: prev.blocks.map(block =>
        block.id === blockId && block.items
          ? { ...block, items: block.items.map((item, i) => i === itemIndex ? content : item) }
          : block
      )
    }));
  };

  // Add list item
  const addListItem = (blockId: string) => {
    setArticle(prev => ({
      ...prev,
      blocks: prev.blocks.map(block =>
        block.id === blockId && block.items
          ? { ...block, items: [...block.items, ''] }
          : block
      )
    }));
  };

  // Remove list item
  const removeListItem = (blockId: string, itemIndex: number) => {
    setArticle(prev => ({
      ...prev,
      blocks: prev.blocks.map(block =>
        block.id === blockId && block.items && block.items.length > 1
          ? { ...block, items: block.items.filter((_, i) => i !== itemIndex) }
          : block
      )
    }));
  };

  // Table functions
  const updateTableCell = (blockId: string, rowIndex: number, colIndex: number, content: string) => {
    setArticle(prev => ({
      ...prev,
      blocks: prev.blocks.map(block => {
        if (block.id === blockId && block.tableData) {
          const newTableData = block.tableData.map((row, ri) =>
            row.map((cell, ci) =>
              ri === rowIndex && ci === colIndex ? { ...cell, content } : cell
            )
          );
          return { ...block, tableData: newTableData };
        }
        return block;
      })
    }));
  };

  const addTableRow = (blockId: string) => {
    setArticle(prev => ({
      ...prev,
      blocks: prev.blocks.map(block => {
        if (block.id === blockId && block.tableData && block.tableData.length > 0) {
          const cols = block.tableData[0].length;
          const newRow: TableCell[] = Array(cols).fill(null).map(() => ({ content: '' }));
          return { ...block, tableData: [...block.tableData, newRow] };
        }
        return block;
      })
    }));
  };

  const addTableColumn = (blockId: string) => {
    setArticle(prev => ({
      ...prev,
      blocks: prev.blocks.map(block => {
        if (block.id === blockId && block.tableData) {
          const newTableData = block.tableData.map((row, rowIndex) => [
            ...row,
            { content: '', isHeader: rowIndex === 0 }
          ]);
          return { ...block, tableData: newTableData };
        }
        return block;
      })
    }));
  };

  const removeTableRow = (blockId: string, rowIndex: number) => {
    setArticle(prev => ({
      ...prev,
      blocks: prev.blocks.map(block => {
        if (block.id === blockId && block.tableData && block.tableData.length > 1) {
          return { ...block, tableData: block.tableData.filter((_, i) => i !== rowIndex) };
        }
        return block;
      })
    }));
  };

  const removeTableColumn = (blockId: string, colIndex: number) => {
    setArticle(prev => ({
      ...prev,
      blocks: prev.blocks.map(block => {
        if (block.id === blockId && block.tableData && block.tableData[0]?.length > 1) {
          const newTableData = block.tableData.map(row => row.filter((_, i) => i !== colIndex));
          return { ...block, tableData: newTableData };
        }
        return block;
      })
    }));
  };

  // Image functions
  const updateImageData = (blockId: string, updates: Partial<ImageData>) => {
    setArticle(prev => ({
      ...prev,
      blocks: prev.blocks.map(block => {
        if (block.id === blockId && block.imageData) {
          return { ...block, imageData: { ...block.imageData, ...updates } };
        }
        return block;
      })
    }));
  };

  const handleImageUpload = (blockId: string, file: File) => {
    // Create a local URL for preview (in production, upload to server/S3)
    const reader = new FileReader();
    reader.onload = (e) => {
      const src = e.target?.result as string;
      updateImageData(blockId, { src, alt: file.name });
    };
    reader.readAsDataURL(file);
  };

  const handleImageUrlSubmit = (blockId: string, url: string) => {
    updateImageData(blockId, { src: url });
  };

  // Article linking functions
  const openArticleLinkModal = () => {
    // Save the current selection before opening modal
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      setSavedSelection(range.cloneRange());
    }
    setArticleLinkModal({
      show: true,
      searchQuery: '',
      selectedTags: []
    });
    setSelectionToolbar(prev => ({ ...prev, show: false }));
  };

  const closeArticleLinkModal = () => {
    setArticleLinkModal({
      show: false,
      searchQuery: '',
      selectedTags: []
    });
    setSavedSelection(null);
  };

  const insertArticleLink = (linkedArticle: LinkedArticle) => {
    if (savedSelection) {
      // Restore the saved selection
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(savedSelection);

        // Get the selected text or use article title as link text
        const selectedText = selection.toString().trim() || linkedArticle.title;

        // Create the link HTML
        const linkHtml = `<a href="/app/ds-wiki/articles/${linkedArticle.id}" class="text-blue-600 hover:text-blue-800 underline" data-article-id="${linkedArticle.id}" data-article-title="${linkedArticle.title}">${selectedText}</a>`;

        // Insert the link
        document.execCommand('insertHTML', false, linkHtml);
      }
    }
    closeArticleLinkModal();
  };

  // Filter articles based on search query and selected tags
  const filteredArticles = existingArticles.filter(article => {
    // Don't show current article in the list
    if (article.id === articleId) return false;

    // Filter by search query
    const matchesSearch = articleLinkModal.searchQuery === '' ||
      article.title.toLowerCase().includes(articleLinkModal.searchQuery.toLowerCase());

    // Filter by selected tags
    const matchesTags = articleLinkModal.selectedTags.length === 0 ||
      articleLinkModal.selectedTags.some(tag => article.tags.includes(tag));

    return matchesSearch && matchesTags;
  });

  const toggleTagFilter = (tag: string) => {
    setArticleLinkModal(prev => ({
      ...prev,
      selectedTags: prev.selectedTags.includes(tag)
        ? prev.selectedTags.filter(t => t !== tag)
        : [...prev.selectedTags, tag]
    }));
  };

  // Handle text selection for formatting toolbar
  const handleTextSelection = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.toString().trim().length > 0) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();

      // Check if selection is within our editor
      if (editorRef.current?.contains(range.commonAncestorContainer)) {
        setSelectionToolbar({
          show: true,
          x: rect.left + rect.width / 2,
          y: rect.top - 10,
          blockId: activeBlockId
        });
      }
    } else {
      setSelectionToolbar(prev => ({ ...prev, show: false }));
    }
  }, [activeBlockId]);

  // Apply text formatting
  const applyFormat = (format: 'bold' | 'italic' | 'underline') => {
    document.execCommand(format, false);
    setSelectionToolbar(prev => ({ ...prev, show: false }));
  };

  // Listen for selection changes
  useEffect(() => {
    document.addEventListener('selectionchange', handleTextSelection);
    return () => document.removeEventListener('selectionchange', handleTextSelection);
  }, [handleTextSelection]);

  // Close toolbar on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (selectionToolbar.show) {
        const toolbar = document.getElementById('formatting-toolbar');
        if (toolbar && !toolbar.contains(e.target as Node)) {
          // Don't close immediately if clicking within editor
          setTimeout(() => {
            const selection = window.getSelection();
            if (!selection || selection.toString().trim().length === 0) {
              setSelectionToolbar(prev => ({ ...prev, show: false }));
            }
          }, 100);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectionToolbar.show]);

  // Save article
  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Sync markdown to blocks if in markdown mode
      if (editorMode === 'markdown') {
        setArticle(prev => ({ ...prev, blocks: markdownToBlocks(markdownContent) }));
      }
      console.log('Saving article:', article);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Render block in visual editor
  const renderBlock = (block: ContentBlock, index: number) => {
    const isActive = activeBlockId === block.id;

    const blockTypeIcon: Record<ContentBlock['type'], string> = {
      h1: 'ri-h-1',
      h2: 'ri-h-2',
      h3: 'ri-h-3',
      paragraph: 'ri-paragraph',
      list: 'ri-list-unordered',
      code: 'ri-code-s-slash-line',
      quote: 'ri-double-quotes-l',
      divider: 'ri-separator',
      table: 'ri-table-line',
      image: 'ri-image-line'
    };

    const blockTypeLabel: Record<ContentBlock['type'], string> = {
      h1: 'Heading 1',
      h2: 'Heading 2',
      h3: 'Heading 3',
      paragraph: 'Paragraph',
      list: 'Bullet List',
      code: 'Code Block',
      quote: 'Quote',
      divider: 'Divider',
      table: 'Table',
      image: 'Image'
    };

    return (
      <div
        key={block.id}
        className={`group relative rounded-lg transition-all duration-200 ${
          isActive ? 'bg-blue-50 ring-2 ring-blue-300' : 'hover:bg-gray-50'
        }`}
        onClick={() => setActiveBlockId(block.id)}
      >
        {/* Block Controls - Left Side */}
        <div className={`absolute -left-10 top-1/2 -translate-y-1/2 flex flex-col gap-1 transition-opacity ${
          isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`}>
          <button
            onClick={(e) => { e.stopPropagation(); moveBlock(block.id, 'up'); }}
            className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
            title="Move up"
          >
            <i className="ri-arrow-up-s-line"></i>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); moveBlock(block.id, 'down'); }}
            className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded"
            title="Move down"
          >
            <i className="ri-arrow-down-s-line"></i>
          </button>
        </div>

        {/* Block Type Selector - Right Side */}
        <div className={`absolute -right-32 top-0 flex items-center gap-2 transition-opacity ${
          isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`}>
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowBlockMenu(showBlockMenu && activeBlockId === block.id ? false : true);
                setActiveBlockId(block.id);
              }}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <i className={`${blockTypeIcon[block.type]} text-sm`}></i>
              <span>{blockTypeLabel[block.type]}</span>
              <i className="ri-arrow-down-s-line"></i>
            </button>

            {/* Block Type Menu */}
            {showBlockMenu && activeBlockId === block.id && (
              <div className="absolute top-full right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-80 overflow-y-auto">
                {(['h1', 'h2', 'h3', 'paragraph', 'list', 'code', 'quote', 'divider', 'table', 'image'] as const).map(type => (
                  <button
                    key={type}
                    onClick={(e) => { e.stopPropagation(); changeBlockType(block.id, type); }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 ${
                      block.type === type ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                    }`}
                  >
                    <i className={`${blockTypeIcon[type]}`}></i>
                    {blockTypeLabel[type]}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); deleteBlock(block.id); }}
            className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
            title="Delete block"
          >
            <i className="ri-delete-bin-line text-sm"></i>
          </button>
        </div>

        {/* Block Content */}
        <div className="py-2 px-3">
          {block.type === 'h1' && (
            <input
              type="text"
              value={block.content}
              onChange={(e) => updateBlock(block.id, e.target.value)}
              className="w-full text-3xl font-bold text-gray-900 bg-transparent border-none outline-none focus:ring-0"
              placeholder="Heading 1"
            />
          )}

          {block.type === 'h2' && (
            <input
              type="text"
              value={block.content}
              onChange={(e) => updateBlock(block.id, e.target.value)}
              className="w-full text-2xl font-bold text-gray-900 bg-transparent border-none outline-none focus:ring-0"
              placeholder="Heading 2"
            />
          )}

          {block.type === 'h3' && (
            <input
              type="text"
              value={block.content}
              onChange={(e) => updateBlock(block.id, e.target.value)}
              className="w-full text-xl font-semibold text-gray-900 bg-transparent border-none outline-none focus:ring-0"
              placeholder="Heading 3"
            />
          )}

          {block.type === 'paragraph' && (
            <textarea
              value={block.content}
              onChange={(e) => updateBlock(block.id, e.target.value)}
              className="w-full text-gray-700 bg-transparent border-none outline-none focus:ring-0 resize-none min-h-[60px]"
              placeholder="Start typing..."
              rows={Math.max(2, Math.ceil(block.content.length / 80))}
            />
          )}

          {block.type === 'list' && (
            <div className="space-y-2">
              {block.items?.map((item, itemIndex) => (
                <div key={itemIndex} className="flex items-start gap-2">
                  <span className="text-gray-400 mt-1">•</span>
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => updateListItem(block.id, itemIndex, e.target.value)}
                    className="flex-1 text-gray-700 bg-transparent border-none outline-none focus:ring-0"
                    placeholder="List item"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addListItem(block.id);
                      }
                      if (e.key === 'Backspace' && !item && block.items && block.items.length > 1) {
                        e.preventDefault();
                        removeListItem(block.id, itemIndex);
                      }
                    }}
                  />
                  {block.items && block.items.length > 1 && (
                    <button
                      onClick={() => removeListItem(block.id, itemIndex)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => addListItem(block.id)}
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                <i className="ri-add-line"></i> Add item
              </button>
            </div>
          )}

          {block.type === 'code' && (
            <div className="bg-gray-900 rounded-lg p-4">
              <textarea
                value={block.content}
                onChange={(e) => updateBlock(block.id, e.target.value)}
                className="w-full font-mono text-sm text-green-400 bg-transparent border-none outline-none focus:ring-0 resize-none"
                placeholder="// Enter code here..."
                rows={Math.max(3, block.content.split('\n').length)}
              />
            </div>
          )}

          {block.type === 'quote' && (
            <div className="border-l-4 border-blue-500 pl-4">
              <textarea
                value={block.content}
                onChange={(e) => updateBlock(block.id, e.target.value)}
                className="w-full text-gray-600 italic bg-transparent border-none outline-none focus:ring-0 resize-none"
                placeholder="Enter quote..."
                rows={2}
              />
            </div>
          )}

          {block.type === 'divider' && (
            <hr className="border-gray-300 my-4" />
          )}

          {block.type === 'table' && block.tableData && (
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse border border-gray-300 rounded-lg overflow-hidden">
                <tbody>
                  {block.tableData.map((row, rowIndex) => (
                    <tr key={rowIndex} className={rowIndex === 0 ? 'bg-gray-100' : 'bg-white'}>
                      {row.map((cell, colIndex) => (
                        <td
                          key={colIndex}
                          className={`border border-gray-300 px-3 py-2 ${
                            rowIndex === 0 ? 'font-semibold text-gray-900' : 'text-gray-700'
                          }`}
                        >
                          <input
                            type="text"
                            value={cell.content}
                            onChange={(e) => updateTableCell(block.id, rowIndex, colIndex, e.target.value)}
                            className={`w-full bg-transparent border-none outline-none focus:ring-0 ${
                              rowIndex === 0 ? 'font-semibold' : ''
                            }`}
                            placeholder={rowIndex === 0 ? 'Header' : 'Cell'}
                          />
                        </td>
                      ))}
                      {isActive && (
                        <td className="border border-gray-200 px-1 bg-gray-50">
                          <button
                            onClick={(e) => { e.stopPropagation(); removeTableRow(block.id, rowIndex); }}
                            className="text-gray-400 hover:text-red-500 p-1"
                            title="Remove row"
                          >
                            <i className="ri-delete-bin-line text-xs"></i>
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {isActive && (
                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); addTableRow(block.id); }}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <i className="ri-add-line"></i> Add Row
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); addTableColumn(block.id); }}
                    className="text-sm text-green-600 hover:text-green-700 flex items-center gap-1"
                  >
                    <i className="ri-add-line"></i> Add Column
                  </button>
                  {block.tableData[0] && block.tableData[0].length > 1 && (
                    <button
                      onClick={(e) => { e.stopPropagation(); removeTableColumn(block.id, block.tableData!.length > 0 ? block.tableData![0].length - 1 : 0); }}
                      className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                    >
                      <i className="ri-subtract-line"></i> Remove Column
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {block.type === 'image' && (
            <div className="space-y-3">
              {block.imageData?.src ? (
                <div className={`relative ${
                  block.imageData.alignment === 'left' ? 'text-left' :
                  block.imageData.alignment === 'right' ? 'text-right' :
                  block.imageData.alignment === 'full' ? '' : 'text-center'
                }`}>
                  <img
                    src={block.imageData.src}
                    alt={block.imageData.alt || ''}
                    className={`rounded-lg ${
                      block.imageData.alignment === 'full' ? 'w-full' : 'max-w-md inline-block'
                    }`}
                  />
                  {isActive && (
                    <button
                      onClick={(e) => { e.stopPropagation(); updateImageData(block.id, { src: '' }); }}
                      className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      title="Remove image"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
                  <i className="ri-image-add-line text-4xl text-gray-400 mb-3 block"></i>
                  <p className="text-sm text-gray-600 mb-4">Add an image to your article</p>
                  <div className="flex items-center justify-center gap-3">
                    <label className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg cursor-pointer hover:bg-blue-700">
                      <i className="ri-upload-2-line mr-2"></i>
                      Upload Image
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleImageUpload(block.id, file);
                        }}
                      />
                    </label>
                    <span className="text-gray-400">or</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const url = prompt('Enter image URL:');
                        if (url) handleImageUrlSubmit(block.id, url);
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300"
                    >
                      <i className="ri-link mr-2"></i>
                      Paste URL
                    </button>
                  </div>
                </div>
              )}

              {/* Image settings - shown when image exists and block is active */}
              {block.imageData?.src && isActive && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500 block mb-1">Alt Text</label>
                    <input
                      type="text"
                      value={block.imageData.alt || ''}
                      onChange={(e) => updateImageData(block.id, { alt: e.target.value })}
                      placeholder="Describe the image..."
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 block mb-1">Caption</label>
                    <input
                      type="text"
                      value={block.imageData.caption || ''}
                      onChange={(e) => updateImageData(block.id, { caption: e.target.value })}
                      placeholder="Add a caption (optional)..."
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 block mb-1">Alignment</label>
                    <div className="flex items-center gap-2">
                      {(['left', 'center', 'right', 'full'] as const).map(align => (
                        <button
                          key={align}
                          onClick={(e) => { e.stopPropagation(); updateImageData(block.id, { alignment: align }); }}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg capitalize transition-colors ${
                            block.imageData?.alignment === align
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {align}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Show caption below image if exists */}
              {block.imageData?.src && block.imageData?.caption && !isActive && (
                <p className="text-sm text-gray-500 text-center italic">{block.imageData.caption}</p>
              )}
            </div>
          )}
        </div>

        {/* Add Block Button - Below */}
        <div className={`absolute -bottom-3 left-1/2 -translate-x-1/2 transition-opacity ${
          isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`}>
          <button
            onClick={(e) => { e.stopPropagation(); addBlockAfter(block.id); }}
            className="w-6 h-6 flex items-center justify-center bg-blue-600 text-white rounded-full hover:bg-blue-700 shadow-sm"
            title="Add block below"
          >
            <i className="ri-add-line text-sm"></i>
          </button>
        </div>
      </div>
    );
  };

  // Generate table of contents from blocks
  const tableOfContents = article.blocks.filter(b => ['h1', 'h2', 'h3'].includes(b.type));

  // Loading state UI
  if (isLoading) {
    return (
      <PageSkeleton />
    );
  }

  // Error state UI
  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-3xl text-red-600"></i>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Article</h2>
          <p className="text-gray-600 mb-6">{loadError}</p>
          <div className="flex items-center justify-center gap-3">
            <Link
              to="/app/ds-wiki/articles"
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Back to Articles
            </Link>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Left - Breadcrumb & Title */}
            <div className="flex items-center">
              <Link to={isNewArticle ? '/app/ds-wiki/articles' : `/app/ds-wiki/articles/${articleId}`} className="text-gray-500 hover:text-gray-700 mr-4">
                <i className="ri-arrow-left-line text-xl"></i>
              </Link>
              <div>
                <div className="text-xs text-gray-500 mb-0.5">{isNewArticle ? 'New Article' : 'Editing Article'}</div>
                <input
                  type="text"
                  value={article.title}
                  onChange={(e) => setArticle(prev => ({ ...prev, title: e.target.value }))}
                  className="text-lg font-semibold text-gray-900 bg-transparent border-none outline-none focus:ring-0 w-96"
                  placeholder={isNewArticle ? 'Enter article title...' : ''}
                />
              </div>
            </div>

            {/* Center - Editor Mode Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => handleModeSwitch('visual')}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                  editorMode === 'visual'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-edit-line mr-2"></i>
                Visual
              </button>
              <button
                onClick={() => handleModeSwitch('markdown')}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                  editorMode === 'markdown'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-markdown-line mr-2"></i>
                Markdown
              </button>
              <button
                onClick={() => setShowPreview(!showPreview)}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                  showPreview
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-eye-line mr-2"></i>
                Preview
              </button>
            </div>

            {/* Right - Actions */}
            <div className="flex items-center gap-3">
              {/* Undo/Redo Buttons */}
              <div className="flex items-center gap-1 border-r border-gray-200 pr-3">
                <button
                  onClick={undo}
                  disabled={!canUndo}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Undo (Ctrl+Z)"
                >
                  <i className="ri-arrow-go-back-line text-lg"></i>
                </button>
                <button
                  onClick={redo}
                  disabled={!canRedo}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Redo (Ctrl+Shift+Z / Ctrl+Y)"
                >
                  <i className="ri-arrow-go-forward-line text-lg"></i>
                </button>
              </div>

              {lastSaved && (
                <span className="text-xs text-gray-500">
                  Saved {lastSaved.toLocaleTimeString()}
                </span>
              )}
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                onClick={() => {
                  handleSave();
                  navigate(isNewArticle ? '/app/ds-wiki/articles' : `/app/ds-wiki/articles/${articleId}`);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                <i className="ri-check-line mr-2"></i>
                {isNewArticle ? 'Create Article' : 'Publish'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-65px)]">
        {/* Left Sidebar - Document Outline */}
        <div className="w-64 flex-shrink-0 border-r border-gray-200 bg-white overflow-y-auto">
          <div className="p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
              <i className="ri-list-unordered mr-2 text-blue-600"></i>
              Document Outline
            </h3>
            <div className="space-y-1">
              {tableOfContents.map((block, index) => (
                <button
                  key={block.id}
                  onClick={() => setActiveBlockId(block.id)}
                  className={`w-full text-left px-2 py-1.5 text-sm rounded-lg transition-colors ${
                    activeBlockId === block.id
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50'
                  } ${block.type === 'h3' ? 'pl-6' : block.type === 'h2' ? 'pl-4' : ''}`}
                >
                  <span className={`${block.type === 'h1' ? 'font-semibold' : ''}`}>
                    {block.content || `Untitled ${block.type.toUpperCase()}`}
                  </span>
                </button>
              ))}
            </div>

            {/* Quick Add Heading */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Add Section</h4>
              <div className="space-y-1">
                <button
                  onClick={() => {
                    const lastBlock = article.blocks[article.blocks.length - 1];
                    addBlockAfter(lastBlock.id, 'h2');
                  }}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
                >
                  <i className="ri-h-2 text-blue-500"></i>
                  New Section (H2)
                </button>
                <button
                  onClick={() => {
                    const lastBlock = article.blocks[article.blocks.length - 1];
                    addBlockAfter(lastBlock.id, 'h3');
                  }}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-50 rounded-lg"
                >
                  <i className="ri-h-3 text-purple-500"></i>
                  New Subsection (H3)
                </button>
              </div>
            </div>

            {/* Article Meta */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Article Info</h4>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500">Subtitle</label>
                  <input
                    type="text"
                    value={article.subtitle}
                    onChange={(e) => setArticle(prev => ({ ...prev, subtitle: e.target.value }))}
                    className="w-full mt-1 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Summary</label>
                  <textarea
                    value={article.summary}
                    onChange={(e) => setArticle(prev => ({ ...prev, summary: e.target.value }))}
                    rows={3}
                    className="w-full mt-1 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Status</label>
                  <select
                    value={article.status}
                    onChange={(e) => setArticle(prev => ({ ...prev, status: e.target.value as ArticleData['status'] }))}
                    className="w-full mt-1 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="draft">Draft</option>
                    <option value="under_curation">Under Review</option>
                    <option value="published">Published</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Editor Area */}
        <div className={`flex-1 overflow-y-auto ${showPreview ? 'flex' : ''}`} ref={editorRef}>
          {/* Floating Formatting Toolbar */}
          {selectionToolbar.show && (
            <div
              id="formatting-toolbar"
              className="fixed z-50 bg-gray-900 text-white rounded-lg shadow-xl flex items-center gap-1 px-2 py-1.5 animate-fade-in"
              style={{
                left: `${selectionToolbar.x}px`,
                top: `${selectionToolbar.y}px`,
                transform: 'translate(-50%, -100%)'
              }}
            >
              <button
                onClick={() => applyFormat('bold')}
                className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-700 transition-colors"
                title="Bold (Ctrl+B)"
              >
                <i className="ri-bold text-lg"></i>
              </button>
              <button
                onClick={() => applyFormat('italic')}
                className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-700 transition-colors"
                title="Italic (Ctrl+I)"
              >
                <i className="ri-italic text-lg"></i>
              </button>
              <button
                onClick={() => applyFormat('underline')}
                className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-700 transition-colors"
                title="Underline (Ctrl+U)"
              >
                <i className="ri-underline text-lg"></i>
              </button>
              <div className="w-px h-5 bg-gray-600 mx-1"></div>
              <button
                onClick={() => document.execCommand('strikeThrough', false)}
                className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-700 transition-colors"
                title="Strikethrough"
              >
                <i className="ri-strikethrough text-lg"></i>
              </button>
              <div className="w-px h-5 bg-gray-600 mx-1"></div>
              <button
                onClick={openArticleLinkModal}
                className="w-8 h-8 flex items-center justify-center rounded hover:bg-gray-700 transition-colors"
                title="Link to Article"
              >
                <i className="ri-link text-lg"></i>
              </button>
            </div>
          )}

          {/* Article Link Modal */}
          {articleLinkModal.show && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
                {/* Modal Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Link to Article</h3>
                    <p className="text-sm text-gray-500 mt-0.5">Select an article to create a link</p>
                  </div>
                  <button
                    onClick={closeArticleLinkModal}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>

                {/* Search and Filter */}
                <div className="px-6 py-4 border-b border-gray-200 space-y-3">
                  {/* Search Input */}
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="text"
                      value={articleLinkModal.searchQuery}
                      onChange={(e) => setArticleLinkModal(prev => ({ ...prev, searchQuery: e.target.value }))}
                      placeholder="Search articles by title..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      autoFocus
                    />
                  </div>

                  {/* Tag Filters */}
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-2">Filter by tags:</div>
                    <div className="flex flex-wrap gap-2">
                      {allAvailableTags.map(tag => (
                        <button
                          key={tag}
                          onClick={() => toggleTagFilter(tag)}
                          className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                            articleLinkModal.selectedTags.includes(tag)
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Active Filters */}
                  {articleLinkModal.selectedTags.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Active filters:</span>
                      <button
                        onClick={() => setArticleLinkModal(prev => ({ ...prev, selectedTags: [] }))}
                        className="text-xs text-blue-600 hover:text-blue-700"
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>

                {/* Articles List */}
                <div className="flex-1 overflow-y-auto px-6 py-4">
                  {filteredArticles.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <i className="ri-file-search-line text-4xl mb-2 block"></i>
                      <p>No articles found matching your criteria</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredArticles.map(article => (
                        <button
                          key={article.id}
                          onClick={() => insertArticleLink(article)}
                          className="w-full text-left p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors group"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900 group-hover:text-blue-700">
                                  {article.title}
                                </span>
                                <span className={`px-2 py-0.5 text-xs rounded-full ${
                                  article.status === 'published' ? 'bg-green-100 text-green-700' :
                                  article.status === 'draft' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-blue-100 text-blue-700'
                                }`}>
                                  {article.status.replace('_', ' ')}
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {article.tags.map(tag => (
                                  <span
                                    key={tag}
                                    className={`px-2 py-0.5 text-xs rounded-full ${
                                      articleLinkModal.selectedTags.includes(tag)
                                        ? 'bg-blue-100 text-blue-700'
                                        : 'bg-gray-100 text-gray-500'
                                    }`}
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <i className="ri-arrow-right-line text-gray-400 group-hover:text-blue-600 mt-1"></i>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      {filteredArticles.length} article{filteredArticles.length !== 1 ? 's' : ''} available
                    </span>
                    <button
                      onClick={closeArticleLinkModal}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Editor */}
          <div className={`${showPreview ? 'w-1/2 border-r border-gray-200' : 'w-full'} bg-white`}>
            <div className="py-8 px-8">
              {editorMode === 'visual' ? (
                /* Visual Editor */
                <div className="space-y-4 pl-12 pr-36 max-w-full">
                  {article.blocks.map((block, index) => renderBlock(block, index))}

                  {/* Add First Block Button (if empty) */}
                  {article.blocks.length === 0 && (
                    <button
                      onClick={() => {
                        const newBlock: ContentBlock = { id: 'first', type: 'paragraph', content: '' };
                        setArticle(prev => ({ ...prev, blocks: [newBlock] }));
                      }}
                      className="w-full py-8 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
                    >
                      <i className="ri-add-line mr-2"></i>
                      Click to start writing
                    </button>
                  )}
                </div>
              ) : (
                /* Markdown Editor */
                <div className="h-full">
                  <div className="mb-4 flex items-center gap-2 text-sm text-gray-500">
                    <i className="ri-markdown-line"></i>
                    <span>Markdown Mode - Use # for headings, - for lists, ``` for code blocks</span>
                  </div>
                  <textarea
                    value={markdownContent}
                    onChange={(e) => setMarkdownContent(e.target.value)}
                    className="w-full h-[calc(100vh-200px)] font-mono text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-lg p-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="# Start writing in Markdown..."
                  />
                </div>
              )}
            </div>
          </div>

          {/* Preview Panel */}
          {showPreview && (
            <div className="w-1/2 bg-gray-50 overflow-y-auto">
              <div className="max-w-3xl mx-auto py-8 px-12">
                <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{article.title}</h1>
                  {article.subtitle && (
                    <p className="text-lg text-gray-600 mb-6">{article.subtitle}</p>
                  )}
                  <div className="prose prose-lg max-w-none">
                    {(editorMode === 'visual' ? article.blocks : markdownToBlocks(markdownContent)).map(block => {
                      switch (block.type) {
                        case 'h1': return <h1 key={block.id} className="text-3xl font-bold mt-8 mb-4">{block.content}</h1>;
                        case 'h2': return <h2 key={block.id} className="text-2xl font-bold mt-6 mb-3">{block.content}</h2>;
                        case 'h3': return <h3 key={block.id} className="text-xl font-semibold mt-4 mb-2">{block.content}</h3>;
                        case 'paragraph': return <p key={block.id} className="mb-4">{block.content}</p>;
                        case 'list': return (
                          <ul key={block.id} className="list-disc ml-6 mb-4">
                            {block.items?.map((item, i) => <li key={i}>{item}</li>)}
                          </ul>
                        );
                        case 'code': return (
                          <pre key={block.id} className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto mb-4">
                            <code>{block.content}</code>
                          </pre>
                        );
                        case 'quote': return (
                          <blockquote key={block.id} className="border-l-4 border-blue-500 pl-4 italic text-gray-600 mb-4">
                            {block.content}
                          </blockquote>
                        );
                        case 'divider': return <hr key={block.id} className="my-6 border-gray-300" />;
                        case 'table': return block.tableData ? (
                          <div key={block.id} className="overflow-x-auto mb-4">
                            <table className="min-w-full border-collapse border border-gray-300 rounded-lg">
                              <tbody>
                                {block.tableData.map((row, rowIndex) => (
                                  <tr key={rowIndex} className={rowIndex === 0 ? 'bg-gray-100' : 'bg-white'}>
                                    {row.map((cell, colIndex) => (
                                      <td
                                        key={colIndex}
                                        className={`border border-gray-300 px-3 py-2 ${
                                          rowIndex === 0 ? 'font-semibold text-gray-900' : 'text-gray-700'
                                        }`}
                                      >
                                        {cell.content}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : null;
                        case 'image': return block.imageData?.src ? (
                          <figure key={block.id} className={`mb-4 ${
                            block.imageData.alignment === 'left' ? 'text-left' :
                            block.imageData.alignment === 'right' ? 'text-right' :
                            block.imageData.alignment === 'full' ? '' : 'text-center'
                          }`}>
                            <img
                              src={block.imageData.src}
                              alt={block.imageData.alt || ''}
                              className={`rounded-lg ${
                                block.imageData.alignment === 'full' ? 'w-full' : 'max-w-md inline-block'
                              }`}
                            />
                            {block.imageData.caption && (
                              <figcaption className="text-sm text-gray-500 mt-2 italic">
                                {block.imageData.caption}
                              </figcaption>
                            )}
                          </figure>
                        ) : null;
                        default: return null;
                      }
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
